/**
 * @prettier
 */

import { getCardSettings } from 'cards/util/cardSettingsUtils';
import { HIGHEST_ALLOWED_MIN_REQUIREMENT, MIN_DPI } from '../constants/Cards';
import { CARD_TYPES, PRIMARY_CUSTOMIZATION_TYPES } from '../constants/CardTypes';
import { STEPS_ALLOWING_MULTIPLES } from '../constants/Steps';
import {
  REGULAR_LISTS_OPTIONS,
  LETTERPRESS_LISTS_OPTIONS,
} from '../components/Customization/common/ColorPicker/constants';
import featureFlags from '../../util/featureFlags';

const cardTypesBySlug = Object.values(CARD_TYPES).reduce((acc, typeKey) => {
  const { urlSlug } = getCardSettings(typeKey);
  if (urlSlug) {
    acc[urlSlug] = typeKey;
  }
  return acc;
}, {});

export const getCardTypeFromSlug = slug => (slug && cardTypesBySlug[slug]) || null;

export function getStepKey(id, cardType) {
  const needsType = STEPS_ALLOWING_MULTIPLES.includes(id) && !!cardType;

  return needsType ? `${id}-${cardType}` : id;
}

export function getStepFromPathname(pathname, orderedSteps) {
  const paths = pathname.split('/');
  const slug = paths[paths.length - 2];
  const stepId = paths[paths.length - 1];
  const cardType = getCardTypeFromSlug(slug);

  let currentStep;
  if (cardType) {
    // Find the step with matching type and id
    const stepKey = getStepKey(stepId, cardType);
    currentStep = orderedSteps.find(step => step.key === stepKey);
  } else {
    // Otherwise, find the first step with matching id
    currentStep = orderedSteps.find(step => step.id === stepId);
  }

  return currentStep;
}

// IMPORTANT: This is a hack used during sku migration to get around multiple customizations with the "ENVELOPE" type.
export function getTypeFromCustomization(customization) {
  switch (customization.type) {
    case 'DETAILS':
      return CARD_TYPES.enclosure;
    case 'ENVELOPE':
      if (/rsvp/i.test(customization.name)) {
        return CARD_TYPES.rsvpEnvelope;
      }
      return CARD_TYPES.invitationEnvelope;
    default:
      return customization.type;
  }
}

export function getBackendTypeFromType(cardType) {
  return cardType === CARD_TYPES.enclosure ? 'DETAILS' : cardType;
}

export function findLeadCustomization(allCustomizations) {
  const customizationWithLeadFlag = allCustomizations.find(
    c => featureFlags.get('enclosuresInInvitesFlow') && c.lead
  );
  const firstPrimaryCustomization = allCustomizations.find(
    c => !c.inactive && PRIMARY_CUSTOMIZATION_TYPES.includes(getTypeFromCustomization(c))
  );

  return customizationWithLeadFlag || firstPrimaryCustomization || {};
}

export function getColorLabelFromCard(cardsData, customizationUUID) {
  const cardUUID = Object.keys(cardsData).find(
    card => cardsData[card].customizationUUID === customizationUUID
  );
  const { optionGroupsByKey: { color: { options = [] } = {} } = {} } = cardsData[cardUUID] || {};
  return options;
}

export function findColorLabel(cardsData, customizationUUID, colorHex) {
  const colorOptions = getColorLabelFromCard(cardsData, customizationUUID);
  const colorData = colorOptions.length && colorOptions.find(option => option.value === colorHex);
  return colorData && colorData.label;
}

export function findEnvelopeFullColorLabel(cardsData, customizationUUID, colorHex, hasLetterpress) {
  const colorLabel = findColorLabel(cardsData, customizationUUID, colorHex);
  const categoriesData = hasLetterpress ? LETTERPRESS_LISTS_OPTIONS : REGULAR_LISTS_OPTIONS;
  const category =
    colorHex.toLowerCase() === 'ffffff' ? categoriesData[0].title : categoriesData[1].title;

  return `${colorLabel ? `${colorLabel} envelope` : 'Envelope'} (${category})`;
}

export const getMinImageSize = (element, page, cardPhysicalDimensions) => {
  const pageHeightPt = page.folded ? page.height / 2 : page.height;
  const pageWidthPt = page.width;
  const { height: elementHeightPt, width: elementWidthPt } = element;

  const pageHeightInches = Math[pageHeightPt > pageWidthPt ? 'max' : 'min'](
    ...cardPhysicalDimensions
  );
  const pageWidthInches = Math[pageWidthPt > pageHeightPt ? 'max' : 'min'](
    ...cardPhysicalDimensions
  );

  let minHeightPx = (element.height / pageHeightPt) * pageHeightInches * MIN_DPI;
  let minWidthPx = (elementWidthPt / pageWidthPt) * pageWidthInches * MIN_DPI;

  // We set a "highest allowed minimum dimension" (even if lower than the min DPI) because users don't
  // always have large images. Most images (except "full-card" images) will still meet the min DPI.
  if (minHeightPx >= minWidthPx && minHeightPx > HIGHEST_ALLOWED_MIN_REQUIREMENT) {
    minWidthPx = (elementWidthPt * HIGHEST_ALLOWED_MIN_REQUIREMENT) / elementHeightPt;
    minHeightPx = HIGHEST_ALLOWED_MIN_REQUIREMENT;
  } else if (minWidthPx > minHeightPx && minWidthPx > HIGHEST_ALLOWED_MIN_REQUIREMENT) {
    minHeightPx = (elementHeightPt * HIGHEST_ALLOWED_MIN_REQUIREMENT) / elementWidthPt;
    minWidthPx = HIGHEST_ALLOWED_MIN_REQUIREMENT;
  }

  return {
    height: Math.round(minHeightPx),
    width: Math.round(minWidthPx),
  };
};



// WEBPACK FOOTER //
// ./src/cards/util/customization.js